import { useMemo } from 'react';
import { useAdminStore } from '../store/adminStore';

export const ADMIN_ROLES = {
  SUPER_ADMIN: 'super_admin',
  LEGACY_SUPER_ADMIN: 'superAdmin',
  ADMIN: 'admin',
  SUPPORT: 'support',
  FINANCE: 'finance',
  GST_EDITOR: 'gstEditor',
  COMPLIANCE: 'compliance',
  ANALYST: 'analyst',
  VIEWER: 'viewer',
};

const ROLE_ALIASES = {
  superAdmin: ADMIN_ROLES.SUPER_ADMIN,
  super_admin: ADMIN_ROLES.SUPER_ADMIN,
};

export const ADMIN_PERMISSIONS = {
  viewDashboard: ['super_admin', 'admin', 'support', 'finance', 'gstEditor', 'compliance', 'analyst', 'viewer'],
  merchantActions: ['super_admin', 'admin', 'support'],
  billingPlans: ['super_admin', 'admin', 'finance'],
  gstDraftEdit: ['super_admin', 'gstEditor', 'compliance'],
  gstPublish: ['super_admin', 'compliance'],
  featureFlags: ['super_admin', 'admin'],
  adminUsers: ['super_admin'],
  productConfig: ['super_admin'],
  promoCodes: ['super_admin', 'admin', 'finance'],
  taskQueue: ['super_admin', 'admin'],
  approvals: ['super_admin'],
  analytics: ['super_admin', 'admin', 'support', 'finance', 'compliance', 'analyst', 'viewer'],
  auditLog: ['super_admin', 'admin', 'compliance'],
  integrationsEdit: ['super_admin'],
};

export function normalizeAdminRole(role) {
  return ROLE_ALIASES[role] || role || ADMIN_ROLES.VIEWER;
}

export function canAdmin(role, permission) {
  const normalizedRole = normalizeAdminRole(role);
  return (ADMIN_PERMISSIONS[permission] || []).includes(normalizedRole);
}

export default function useAdminRole() {
  const profile = useAdminStore((state) => state.profile);
  const role = normalizeAdminRole(profile?.role);

  return useMemo(() => {
    const isSuperAdmin = role === ADMIN_ROLES.SUPER_ADMIN;
    const access = (permission) => canAdmin(role, permission);

    return {
      profile,
      role,
      isSuperAdmin,
      isAdmin: isSuperAdmin || role === ADMIN_ROLES.ADMIN,
      isSupport: role === ADMIN_ROLES.SUPPORT,
      isFinance: role === ADMIN_ROLES.FINANCE,
      isGstEditor: role === ADMIN_ROLES.GST_EDITOR,
      isCompliance: role === ADMIN_ROLES.COMPLIANCE,
      isAnalyst: role === ADMIN_ROLES.ANALYST,
      isViewer: role === ADMIN_ROLES.VIEWER,
      can: access,
    };
  }, [profile, role]);
}
